// @flow

import React, {Fragment} from 'react';
import {FormattedMessage} from 'react-intl';
import DualPriceTooltip from '../DualPriceTooltip/DualPriceTooltip';
import type {ListingShape} from '../Hit/Hit';
import type {IntlShape} from 'react-intl';
import type {Node} from 'react';
import './DualPrice.less';
import type {PriceSummaryShape} from '../Price/Price';
import PriceItem from '../PriceItem/PriceItem';

export type PriceItemShape = {
    ...PriceSummaryShape,
    roundedFormattedAmount: ?string,
    isPrimary: boolean,
    isPriceBold: boolean,
    withTooltip?: boolean
};

export type PriceLabelShape = {
    priceTypeId: string,
    pricePeriodDescription: string
};

export type ListingPricesShape = {
    nightlyPrice: PriceItemShape,
    totalPrice: PriceItemShape,
    priceLabel: PriceLabelShape
};

const listingHasPriceInfo = (listing: ListingShape): boolean => {
    const {priceSummary, priceSummarySecondary, priceLabel} = listing;

    return (
        priceSummary !== null ||
        priceSummarySecondary !== null ||
        priceLabel !== null
    );
};

const getListingPrices = (
    listing: ListingShape,
    withTooltip?: boolean
): ListingPricesShape => {
    const defaultPrice = {
        priceTypeId: 'UNKNOWN',
        pricePeriodDescription: '',
        formattedAmount: '',
        roundedFormattedAmount: ''
    };
    const defaultLabel = {
        priceTypeId: 'UNKNOWN',
        pricePeriodDescription: ''
    };
    const listingPriceSummary = listing.priceSummary || defaultPrice;
    const listingPriceSummarySecondary =
        listing.priceSummarySecondary || defaultPrice;
    const listingPriceSummaryId = listingPriceSummary.priceTypeId;
    const listingPriceSummarySecondaryId =
        listingPriceSummarySecondary.priceTypeId;
    const priceLabel = listing.priceLabel || defaultLabel;
    let nightlyPrice = {};
    let totalPrice = {};

    if (
        listingPriceSummaryId === 'PER_NIGHT' ||
        listingPriceSummarySecondaryId === 'UNKNOWN'
    ) {
        nightlyPrice = {
            ...listingPriceSummary,
            isPriceBold: true,
            isPrimary: true
        };
        totalPrice = {
            ...listingPriceSummarySecondary,
            isPriceBold: false,
            isPrimary: false,
            withTooltip
        };
    } else {
        totalPrice = {
            ...listingPriceSummary,
            isPriceBold: true,
            isPrimary: true,
            withTooltip
        };
        nightlyPrice = {
            ...listingPriceSummarySecondary,
            isPriceBold: false,
            isPrimary: false
        };
    }

    return {
        nightlyPrice,
        totalPrice,
        priceLabel
    };
};

export const DualPrice = ({
    listing,
    intl,
    isDesktop = true,
    showInstantBookIcon = true,
    withTooltip = false,
    isModalOpen,
    setModalOpen
}: {
    listing: ListingShape,
    intl: IntlShape,
    isDesktop: boolean,
    showInstantBookIcon?: boolean,
    withTooltip?: boolean,
    isModalOpen: boolean,
    setModalOpen: (isModalOpen: boolean) => void
}) => {
    if (!listingHasPriceInfo(listing)) {
        return (
            <span
                className="Price Price--noPrice"
                data-wdio="Price"
                data-price="0"
            >
                <FormattedMessage
                    id={'ratesNone'}
                    description={'no price'}
                    defaultMessage={'View details for price'}
                >
                    {(txt: Node): Node => (
                        <span className="Price__Fallback">{txt}</span>
                    )}
                </FormattedMessage>
            </span>
        );
    }
    const {instantBookable} = listing;
    const {nightlyPrice, totalPrice, priceLabel} = getListingPrices(
        listing,
        withTooltip
    );

    let prices = [nightlyPrice, totalPrice];
    // for mobile view primary item should always be first
    if (!withTooltip && !isDesktop && !prices[0].isPrimary) {
        prices = prices.reverse();
    }

    if (
        withTooltip &&
        totalPrice.formattedAmount &&
        nightlyPrice.formattedAmount
    ) {
        return (
            <DualPriceTooltip
                isDesktop={isDesktop}
                prices={prices}
                priceLabel={priceLabel}
                isModalOpen={isModalOpen}
                setModalOpen={setModalOpen}
                showInstantBookIcon={showInstantBookIcon}
                instantBookable={instantBookable}
                intl={intl}
            />
        );
    }

    return (
        <Fragment>
            {prices.map((price, index) => (
                <PriceItem
                    item={price}
                    key={`price_${index}`}
                    showInstantBookIcon={showInstantBookIcon}
                    instantBookable={instantBookable}
                    intl={intl}
                />
            ))}
            <span className="DualPrice__priceLabel">
                {priceLabel.pricePeriodDescription}
            </span>
        </Fragment>
    );
};

export default DualPrice;
